import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  DISCLOSURE_ACCEPT_LABEL,
  DISCLOSURE_ACKNOWLEDGEMENT,
  DISCLOSURE_LEAD,
  DISCLOSURE_POINTS,
  DISCLOSURE_SUMMARY,
  DISCLOSURE_TITLE,
} from '@shared/disclosure'
import { api, ApiError } from '../lib/api'
import { Banner, Button, Card } from '../ui'

/**
 * What a customer reads before they connect a number.
 *
 * Every word comes from `@shared/disclosure` rather than living here, because the pairing
 * route refuses to start a session without a stored acceptance of a specific version of that
 * text — copy in this file could be edited without the version moving, and a record of
 * acceptance that does not identify what was accepted is worth nothing.
 *
 * The whole thing is on the page. Not a link, not a modal that scrolls to a button, not a
 * summary with "learn more": there is no version of this that is honest and also two lines,
 * and a customer who loses their main WhatsApp number will remember exactly how hard it was
 * to find out that could happen.
 */
export function useDisclosure() {
  return useQuery({ queryKey: ['disclosure'], queryFn: () => api.disclosure() })
}

export function DisclosureNotice({ onAccepted }: { onAccepted?: () => void }) {
  const queryClient = useQueryClient()
  const [acknowledged, setAcknowledged] = useState(false)

  const accept = useMutation({
    mutationFn: () => api.acceptDisclosure(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['disclosure'] })
      onAccepted?.()
    },
  })

  const error = accept.error
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <Card variant="outlined">
      <div className="stack gap-4">
        <div className="stack gap-2">
          <h2 className="title-large">{DISCLOSURE_TITLE}</h2>
          <p className="body-medium text-muted">{DISCLOSURE_LEAD}</p>
        </div>

        <div className="stack gap-3">
          {DISCLOSURE_POINTS.map((point) => (
            <div key={point.heading} className="stack gap-1">
              <span className="title-small">{point.heading}</span>
              <p className="body-small text-muted">{point.body}</p>
            </div>
          ))}
        </div>

        {message && <Banner tone="error">{message}</Banner>}

        <div className="stack gap-3">
          {/* The checkbox gates the button rather than being read alongside it, so accepting
              cannot happen as a single reflexive click on the thing that continues. */}
          <label className="row gap-2" style={{ alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              style={{ marginTop: '4px' }}
            />
            <span className="body-small">{DISCLOSURE_ACKNOWLEDGEMENT}</span>
          </label>
          <div>
            <Button
              onClick={() => accept.mutate()}
              disabled={!acknowledged || accept.isPending}
            >
              {accept.isPending ? 'Saving…' : DISCLOSURE_ACCEPT_LABEL}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

/**
 * The one-line version, for screens that are not the place to read six paragraphs.
 *
 * It exists so that somebody who accepted this months ago and now sees a number go amber is
 * reminded what kind of product they are looking at, rather than having to remember.
 */
export function DisclosureReminder({ acceptedAt }: { acceptedAt: string | null }) {
  return (
    <p className="body-small text-muted">
      {DISCLOSURE_SUMMARY}
      {acceptedAt
        ? ` You accepted this on ${new Date(acceptedAt).toLocaleDateString(undefined, {
            dateStyle: 'medium',
          })}.`
        : ''}
    </p>
  )
}
